import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Sparkles, Send, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const TREND_ICON = { rising: TrendingUp, falling: TrendingDown, flat: Minus };
const TREND_TONE = {
  rising: "bg-[#114232]/8 text-[#114232]",
  falling: "bg-[#c15337]/12 text-[#8b3a23]",
  flat: "bg-[#d49a36]/15 text-[#8a5a16]",
};

export default function CoachWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [traj, setTraj] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/merchant/coach/history").then((r) => setMessages(r.data.messages || []));
    api.get("/merchant/coach/trajectory").then((r) => setTraj(r.data));
  }, [open]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  async function send() {
    const text = (draft || "").trim();
    if (!text) return;
    setSending(true);
    setDraft("");
    setMessages((m) => [...m, { id: "tmp-" + Date.now(), role: "user", content: text }]);
    try {
      const { data: r } = await api.post("/merchant/coach/ask", { message: text });
      setMessages((m) => [...m.filter((x) => !x.id.startsWith("tmp-")), r.user_message, r.reply]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setMessages((m) => m.filter((x) => !x.id.startsWith("tmp-")));
    } finally {
      setSending(false);
    }
  }

  const TrendIcon = traj ? TREND_ICON[traj.trend] || Minus : Minus;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="coach-fab"
        className="fixed bottom-6 right-24 z-40 w-14 h-14 rounded-full bg-[#114232] text-[#f4f2eb] shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
        title="Open Risk Coach"
      >
        <Sparkles className="w-6 h-6" strokeWidth={1.7} />
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="coach-overlay"
        >
          <div className="bg-white w-full sm:max-w-lg h-[85vh] sm:h-[600px] rounded-t-2xl sm:rounded-xl shadow-2xl flex flex-col" data-testid="coach-panel">
            {/* Header + trajectory */}
            <div className="p-4 border-b border-[#114232]/10 flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#c15337]">CyberScore</div>
                <div className="font-heading text-lg font-bold text-[#114232] flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Risk Coach
                </div>
              </div>
              <div className="flex items-center gap-2">
                {traj && (
                  <div
                    className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md flex items-center gap-1 ${TREND_TONE[traj.trend] || ""}`}
                    data-testid="coach-trajectory"
                  >
                    <TrendIcon className="w-3 h-3" />
                    {traj.current_score} → {traj.projected_score} (4w)
                  </div>
                )}
                <button
                  onClick={() => setOpen(false)}
                  data-testid="coach-close"
                  className="text-[#5c6360] hover:text-[#114232] text-2xl leading-none"
                >×</button>
              </div>
            </div>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="coach-messages">
              {messages.length === 0 && (
                <div className="text-center text-sm text-[#5c6360] py-8">
                  Ask me anything about your CyberScore, claims, or how to lower your risk.
                </div>
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`coach-msg-${m.role}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#c15337] text-white rounded-br-sm"
                        : "bg-[#114232]/8 text-[#1b1d1c] rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start" data-testid="coach-typing">
                  <div className="px-3 py-2 rounded-2xl bg-[#114232]/8 text-[#5c6360] text-xs flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
            {/* Composer */}
            <div className="p-3 border-t border-[#114232]/10 flex items-end gap-2">
              <Textarea
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="How can I improve my CyberScore?"
                data-testid="coach-input"
                disabled={sending}
                className="resize-none border-[#114232]/15 text-sm"
              />
              <Button
                onClick={send}
                disabled={sending || !draft.trim()}
                data-testid="coach-send"
                className="btn-baobab rounded-full px-4 h-10"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
